/**
 * Billing helper functions
 */

import type { BillingSubscription } from '@/types/billing'

/**
 * Subscription statuses that grant access to workflow creation.
 * Statuses outside this list (e.g. canceled, past_due, unpaid, incomplete)
 * are treated as "no active subscription" and block workflow creation.
 */
export const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'free_quota_only'] as const

/**
 * Returns true when the subscription is loaded and has an active status.
 * A non-null subscription with a status outside ACTIVE_SUBSCRIPTION_STATUSES
 * (e.g. canceled, past_due) is treated the same as no subscription.
 */
export const isActiveSubscription = (
  subscription: BillingSubscription | null | undefined
): boolean =>
  subscription != null && (ACTIVE_SUBSCRIPTION_STATUSES as readonly string[]).includes(subscription.status)

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)
}

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const getPlanCreditsLimit = (planTier: string): number | string => {
  const limits = {
    starter: 3,
    pro: 10,
    power: 'Unlimited'
  }
  return limits[planTier as keyof typeof limits] || 0
}

export const isUpgrade = (currentPlan: string, newPlan: string): boolean => {
  const planOrder = { starter: 1, pro: 2, power: 3 }
  return planOrder[newPlan as keyof typeof planOrder] > planOrder[currentPlan as keyof typeof planOrder]
}

export const calculateProrationCredit = (
  currentPlanPrice: number,
  newPlanPrice: number,
  daysRemaining: number,
  totalDaysInPeriod: number
): number => {
  const remainingValue = (currentPlanPrice * daysRemaining) / totalDaysInPeriod
  const newPlanProrated = (newPlanPrice * daysRemaining) / totalDaysInPeriod
  return remainingValue - newPlanProrated
}
